/**
 * Shared DOM surfaces for arcade mini-games.
 *
 * These components provide reusable HUD, selector, playfield, blocked-slot,
 * and log chrome. Game rules stay in pure app-local models; this file only
 * standardizes presentation and interaction surfaces.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React from "react";
import { PanelSurface } from "@csk/demo-ui";
import { ArcadeStatPill } from "./ArcadeControls.js";
import { ComboBadge, TimerBar } from "./ArcadeHud.js";

export interface ArcadeHudStat {
  readonly label: string;
  readonly value: string | number;
  readonly color: string;
  readonly title?: string;
}

export interface ArcadeHudTimer {
  readonly fraction: number;
  readonly remaining: number;
  readonly accent: string;
  readonly danger: string;
  readonly track: string;
  readonly running: boolean;
  readonly reducedMotion: boolean;
}

export interface ArcadeHudCombo {
  readonly combo: number;
  readonly color: string;
  readonly muted: string;
  readonly reducedMotion: boolean;
}

export interface ArcadeHudStripProps {
  readonly stats: readonly ArcadeHudStat[];
  readonly score: number;
  readonly rank: string;
  readonly rankColor: string;
  readonly scoreColor: string;
  readonly borderColor: string;
  readonly timer: ArcadeHudTimer;
  readonly combo: ArcadeHudCombo;
  readonly background?: string;
  readonly scoreLabel?: string;
}

export interface ArcadeHudStripModel {
  readonly stats: readonly ArcadeHudStat[];
  readonly score: number;
  readonly rank: string;
  readonly rankColor: string;
  readonly scoreColor: string;
  readonly borderColor: string;
  readonly clock: Pick<ArcadeHudTimer, "fraction" | "remaining" | "running">;
  readonly timerAccent: string;
  readonly timerDanger: string;
  readonly timerTrack: string;
  readonly combo: number;
  readonly comboColor: string;
  readonly comboMuted: string;
  readonly reducedMotion: boolean;
  readonly background?: string;
  readonly scoreLabel?: string;
}

export function createArcadeHudStripProps({
  stats,
  score,
  rank,
  rankColor,
  scoreColor,
  borderColor,
  clock,
  timerAccent,
  timerDanger,
  timerTrack,
  combo,
  comboColor,
  comboMuted,
  reducedMotion,
  background,
  scoreLabel,
}: ArcadeHudStripModel): ArcadeHudStripProps {
  return {
    stats,
    score,
    rank,
    rankColor,
    scoreColor,
    borderColor,
    timer: {
      fraction: clock.fraction,
      remaining: clock.remaining,
      accent: timerAccent,
      danger: timerDanger,
      track: timerTrack,
      running: clock.running,
      reducedMotion,
    },
    combo: {
      combo,
      color: comboColor,
      muted: comboMuted,
      reducedMotion,
    },
    background,
    scoreLabel,
  };
}

export function ArcadeHudStrip({
  stats,
  score,
  rank,
  rankColor,
  scoreColor,
  borderColor,
  timer,
  combo,
  background = "rgba(0,0,0,0.2)",
  scoreLabel = "score",
}: ArcadeHudStripProps): React.JSX.Element {
  return (
    <div
      className="flex flex-wrap items-center gap-2 border-b p-3"
      style={{ background, borderColor }}
    >
      {stats.map((stat) => (
        <ArcadeStatPill
          key={stat.label}
          label={stat.label}
          value={stat.value}
          color={stat.color}
          title={stat.title}
        />
      ))}
      <ArcadeStatPill label="rank" value={rank} color={rankColor} />
      <span
        className="min-w-[88px] font-mono text-[12px] font-bold"
        style={{ color: scoreColor }}
      >
        {scoreLabel} {score}
      </span>
      <div className="min-w-[190px] flex-1">
        <TimerBar
          fraction={timer.fraction}
          remaining={timer.remaining}
          accent={timer.accent}
          danger={timer.danger}
          track={timer.track}
          running={timer.running}
          reducedMotion={timer.reducedMotion}
        />
      </div>
      <ComboBadge
        combo={combo.combo}
        color={combo.color}
        muted={combo.muted}
        reducedMotion={combo.reducedMotion}
      />
    </div>
  );
}

export interface ArcadeLevelOption {
  readonly id: string;
  readonly name: string;
}

export interface ArcadeLevelSelectProps {
  readonly levels: readonly ArcadeLevelOption[];
  readonly activeLevelId: string;
  readonly onSelect: (levelId: string) => void;
  readonly onReset: () => void;
  readonly accentColor: string;
  readonly borderColor: string;
  readonly mutedColor: string;
  readonly brightColor: string;
  readonly activeBackground: string;
  readonly background?: string;
  readonly resetLabel?: string;
}

export function ArcadeLevelSelect({
  levels,
  activeLevelId,
  onSelect,
  onReset,
  accentColor,
  borderColor,
  mutedColor,
  brightColor,
  activeBackground,
  background = "rgba(6,8,16,0.42)",
  resetLabel = "Reset",
}: ArcadeLevelSelectProps): React.JSX.Element {
  return (
    <div className="mb-4 flex flex-wrap items-center gap-2">
      {levels.map((level) => {
        const active = level.id === activeLevelId;
        return (
          <button
            key={level.id}
            type="button"
            onClick={() => {
              onSelect(level.id);
            }}
            className="rounded-full border px-3 py-1.5 text-xs font-bold transition"
            style={{
              background: active ? activeBackground : background,
              borderColor: active ? accentColor : borderColor,
              color: active ? brightColor : mutedColor,
            }}
          >
            {level.name}
          </button>
        );
      })}
      <button
        type="button"
        onClick={onReset}
        className="rounded-full border px-3 py-1.5 text-xs font-bold transition"
        style={{
          background,
          borderColor,
          color: brightColor,
        }}
      >
        {resetLabel}
      </button>
    </div>
  );
}

export interface ArcadePlayfieldFrameProps {
  readonly children: React.ReactNode;
  readonly borderColor: string;
  readonly background: string;
  readonly accentColor: string;
  readonly header?: React.ReactNode;
  readonly className?: string;
  readonly minHeight?: React.CSSProperties["minHeight"];
  readonly secondaryColor?: string;
  readonly gridSize?: number;
}

export function ArcadePlayfieldFrame({
  children,
  borderColor,
  background,
  accentColor,
  header,
  className,
  minHeight = 500,
  secondaryColor = accentColor,
  gridSize = 48,
}: ArcadePlayfieldFrameProps): React.JSX.Element {
  return (
    <section
      className={joinClassNames(
        "relative overflow-hidden rounded-2xl border p-4",
        className,
      )}
      style={{
        background,
        borderColor,
        boxShadow: `inset 0 1px 0 rgba(255,255,255,0.04), 0 0 36px ${accentColor}14`,
        minHeight,
      }}
    >
      <div
        aria-hidden
        className="absolute inset-0 opacity-80"
        style={{
          background: `linear-gradient(135deg, ${accentColor}16, transparent 32%), linear-gradient(225deg, ${secondaryColor}14, transparent 34%), linear-gradient(180deg, rgba(13,19,32,0.18), rgba(2,6,23,0.78))`,
        }}
      />
      <div
        aria-hidden
        className="absolute inset-0"
        style={{
          backgroundImage:
            "linear-gradient(90deg, rgba(148,163,184,0.07) 1px, transparent 1px), linear-gradient(0deg, rgba(148,163,184,0.055) 1px, transparent 1px)",
          backgroundSize: `${gridSize}px ${gridSize}px`,
        }}
      />
      {header ? <div className="relative z-10 mb-4">{header}</div> : null}
      <div className="relative z-10">{children}</div>
    </section>
  );
}

export interface ArcadeArenaStageProps {
  readonly children: React.ReactNode;
  readonly hud?: React.ReactNode;
  readonly hudOverlay?: React.ReactNode;
  readonly hudProps?: ArcadeHudStripProps;
  readonly levelSelect?: React.ReactNode;
  readonly levelSelectProps?: ArcadeLevelSelectProps;
  readonly bodyClassName?: string;
  readonly columnsClassName?: string;
}

export function ArcadeArenaStage({
  hud,
  children,
  hudOverlay,
  hudProps,
  levelSelect,
  levelSelectProps,
  bodyClassName,
  columnsClassName,
}: ArcadeArenaStageProps): React.JSX.Element {
  return (
    <>
      {hudOverlay}
      {hud ?? (hudProps ? <ArcadeHudStrip {...hudProps} /> : null)}
      <div className={joinClassNames("p-4", bodyClassName)}>
        {levelSelect ??
          (levelSelectProps ? (
            <ArcadeLevelSelect {...levelSelectProps} />
          ) : null)}
        <div
          className={joinClassNames(
            "grid gap-4 xl:grid-cols-[minmax(0,1fr)_320px]",
            columnsClassName,
          )}
        >
          {children}
        </div>
      </div>
    </>
  );
}

export interface ArcadeArenaCardProps {
  readonly children: React.ReactNode;
  readonly glowColor?: string;
  readonly className?: string;
  readonly style?: React.CSSProperties;
}

export function ArcadeArenaCard({
  children,
  glowColor,
  className,
  style,
}: ArcadeArenaCardProps): React.JSX.Element {
  return (
    <PanelSurface.Card
      glowColor={glowColor}
      className={className}
      style={{ padding: 0, overflow: "hidden", ...style }}
    >
      {children}
    </PanelSurface.Card>
  );
}

export interface ArcadeBlockedSlotProps {
  readonly label: string;
  readonly title: string;
  readonly detail: string;
  readonly color: string;
  readonly className?: string;
}

export function ArcadeBlockedSlot({
  label,
  title,
  detail,
  color,
  className,
}: ArcadeBlockedSlotProps): React.JSX.Element {
  return (
    <div
      className={joinClassNames(
        "rounded-xl border border-dashed p-3",
        className,
      )}
      style={{
        background: `${color}17`,
        borderColor: `${color}66`,
        color,
      }}
    >
      <div className="font-mono text-[11px] uppercase">{label}</div>
      <div className="mt-2 text-sm font-black text-white">{title}</div>
      <div className="mt-1 text-xs leading-5" style={{ color: `${color}DD` }}>
        {detail}
      </div>
    </div>
  );
}

export interface ArcadeStatusDot {
  readonly label: string;
  readonly active: boolean;
}

export interface ArcadeStatusDotsProps {
  readonly items: readonly ArcadeStatusDot[];
  readonly activeColor: string;
  readonly inactiveColor: string;
  readonly activeText?: string;
  readonly inactiveText?: string;
  readonly className?: string;
}

export function ArcadeStatusDots({
  items,
  activeColor,
  inactiveColor,
  activeText = "ok",
  inactiveText = "no",
  className,
}: ArcadeStatusDotsProps): React.JSX.Element {
  return (
    <div className={joinClassNames("flex flex-wrap gap-1.5", className)}>
      {items.map((item) => {
        const color = item.active ? activeColor : inactiveColor;
        return (
          <span
            key={item.label}
            className="rounded-full border px-2 py-1 font-mono text-[10px] uppercase"
            style={{
              background: `${color}18`,
              borderColor: `${color}66`,
              color,
            }}
          >
            {item.active ? activeText : inactiveText} {item.label}
          </span>
        );
      })}
    </div>
  );
}

export interface ArcadeLogPanelProps {
  readonly title: string;
  readonly event: string;
  readonly borderColor: string;
  readonly background: string;
  readonly textColor: string;
  readonly badges?: readonly React.ReactNode[];
  readonly children?: React.ReactNode;
  readonly className?: string;
}

export function ArcadeLogPanel({
  title,
  event,
  borderColor,
  background,
  textColor,
  badges = [],
  children,
  className,
}: ArcadeLogPanelProps): React.JSX.Element {
  return (
    <aside
      className={joinClassNames("rounded-2xl border p-4", className)}
      style={{ background, borderColor }}
    >
      <div className="font-mono text-[12px] font-black uppercase tracking-[0.08em] text-white">
        {title}
      </div>
      {badges.length > 0 ? (
        <div className="mt-3 flex flex-wrap gap-2">{badges}</div>
      ) : null}
      <div
        aria-live="polite"
        className="mt-4 rounded-xl border bg-black/30 p-3 text-sm leading-6"
        style={{ borderColor, color: textColor }}
      >
        {event}
      </div>
      {children ? <div className="mt-4">{children}</div> : null}
    </aside>
  );
}

function joinClassNames(
  ...classNames: readonly (string | undefined)[]
): string {
  return classNames.filter(Boolean).join(" ");
}
